import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api, ApiRequestError } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { Button, FieldGroup, Input, Label } from "../components/ui";

type Tab = "login" | "register" | "forgot";

export function AuthScreen() {
  const [tab, setTab] = useState<Tab>("login");
  const [err, setErr] = useState<string | null>(null);
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const location = useLocation();
  // RequireAuth attaches the original path when it bounces an unauthenticated
  // visitor (e.g. a full reload mid-game loses the in-memory token, or an
  // expired session): after login/registration we must go back there, not to
  // the lobby browser (F8 fix).
  const from = (location.state as { from?: string } | null)?.from;
  const dest = from ?? "/lobby";
  const login = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) => api.login(username, password),
    onSuccess: (s) => { setSession(s); navigate(dest); },
    onError: (e) => setErr(e instanceof ApiRequestError ? e.message : "Errore di rete"),
  });
  const register = useMutation({
    mutationFn: ({ username, email, password }: { username: string; email: string; password: string }) =>
      api.register(username, email, password),
    onSuccess: (s) => { setSession(s); navigate(dest); },
    onError: (e) => setErr(e instanceof ApiRequestError ? e.message : "Errore di rete"),
  });
  const forgot = useMutation({
    mutationFn: (email: string) => api.forgotPassword(email),
    onSuccess: () => setErr(null),
  });

  return (
    <div className="mx-auto flex min-h-[calc(100vh-3.5rem)] max-w-md flex-col items-center justify-center px-4 py-10">
      <div className="mb-8 text-center">
        <div className="mb-3 text-5xl text-brass">♦</div>
        <h1 className="font-display text-4xl text-ink">Bisca</h1>
      </div>

      <div className="panel w-full p-6">
        <div className="mb-5 flex gap-1 rounded-xl bg-surface-2 p-1">
          {(["login", "register", "forgot"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setErr(null); }}
              className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-caps transition-colors ${
                tab === t ? "bg-brass text-bg" : "text-ink-dim hover:text-ink"
              }`}
            >
              {t === "login" ? "Accedi" : t === "register" ? "Registrati" : "Recupero"}
            </button>
          ))}
        </div>

        {tab === "login" && <LoginForm onSubmit={(v) => login.mutate(v)} loading={login.isPending} err={err} />}
        {tab === "register" && <RegisterForm onSubmit={(v) => register.mutate(v)} loading={register.isPending} err={err} />}
        {tab === "forgot" && (
          <ForgotForm
            onSubmit={(email) => forgot.mutate(email)}
            loading={forgot.isPending}
            done={forgot.isSuccess}
          />
        )}
      </div>
    </div>
  );
}

function Error({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <div className="mb-3 rounded-lg border border-crimson-dim bg-crimson-dim/10 px-3 py-2 text-sm text-crimson">{msg}</div>;
}

function LoginForm({ onSubmit, loading, err }: {
  onSubmit: (v: { username: string; password: string }) => void;
  loading: boolean;
  err: string | null;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ username, password }); }}
      className="flex flex-col gap-4"
    >
      <Error msg={err} />
      <FieldGroup>
        <Label>Username o email</Label>
        <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
      </FieldGroup>
      <FieldGroup>
        <Label>Password</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
      </FieldGroup>
      <Button type="submit" disabled={loading} className="mt-1">{loading ? "…" : "Accedi"}</Button>
    </form>
  );
}

function RegisterForm({ onSubmit, loading, err }: {
  onSubmit: (v: { username: string; email: string; password: string }) => void;
  loading: boolean;
  err: string | null;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localErr, setLocalErr] = useState<string | null>(null);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (password !== confirm) { setLocalErr("Le password non coincidono"); return; }
        setLocalErr(null);
        onSubmit({ username, email, password });
      }}
      className="flex flex-col gap-4"
    >
      <Error msg={localErr ?? err} />
      <FieldGroup>
        <Label>Username (min 3)</Label>
        <Input value={username} maxLength={30} onChange={(e) => setUsername(e.target.value)} required />
      </FieldGroup>
      <FieldGroup>
        <Label>Email</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </FieldGroup>
      <FieldGroup>
        <Label>Password (min 6)</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </FieldGroup>
      <FieldGroup>
        <Label>Conferma password</Label>
        <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
      </FieldGroup>
      <Button type="submit" disabled={loading} className="mt-1">{loading ? "…" : "Crea account"}</Button>
    </form>
  );
}

function ForgotForm({ onSubmit, loading, done }: { onSubmit: (email: string) => void; loading: boolean; done: boolean }) {
  const [email, setEmail] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(email); }} className="flex flex-col gap-4">
      {done ? (
        <p className="rounded-lg border border-emerald/30 bg-emerald/5 px-3 py-3 text-sm text-emerald">
          Se l'email è registrata, riceverai un link di recupero.
        </p>
      ) : (
        <p className="text-sm text-ink-dim">Inserisci la tua email: ti invieremo un link per reimpostare la password.</p>
      )}
      <FieldGroup>
        <Label>Email</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </FieldGroup>
      <Button type="submit" disabled={loading}>{loading ? "…" : "Invia link"}</Button>
    </form>
  );
}
